import { DialogPositioningBehavior, DialogType } from '@tableau/widgets';
import * as React from 'react';
import * as _ from 'underscore';
import {
  getAuthorizableType,
  PermissionsDialog,
  PermissionsDialogProps,
} from '../../app/permissions/PermissionsDialog';
import { PermissionsDialogStyles } from '../../app/permissions/style/PermissionsDialogStyle';
import { Formatters, Messages } from '../../codegen/Localize';
import { MetricContentType } from '../../metrics/models/MetricContentType';
import { AuthorizableType } from '../../permissions/types/AuthorizableType';
import { L10n } from '../../ts/L10n/L10n';
import {
  DatabaseContentType,
  DataRoleContentType,
  DatasourceContentType,
  FlowContentType,
  ProjectContentType,
  PublishedConnectionContentType,
  TableContentType,
  ViewContentType,
  WorkbookContentType,
} from '../../ts/models/ContentTypes';
import { IDataSourceInfo } from '../../ts/models/IDataSourceInfo';
import { Set } from '../../ts/models/Set';
import { IGetActionableObjectsResult } from '../../ts/ServerApi/requests/GetActionableObjectsRequest';
import { INamedItem } from '../../ts/ServerApi/types/resourceParts';
import { GetActionableObjectsForAction } from '../requests/GetActionableObjectsForAction';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { openReactAlertDialog } from '../../commonInfra/dialogs/ReactAlertDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';

export namespace EditPermissionsAction {
  const CannotViewAllSelectedMessages: { [n: string]: (a: { ITEM_COUNT: number, ITEM_NAME: string, ITEM_COUNT_LOC: string}) => string } = {
    [DatabaseContentType.name]: Messages.permissions_cannotViewSelectedDatabases,
    [DatasourceContentType.name]: Messages.permissions_cannotViewSelectedDataSources,
    [FlowContentType.name]: Messages.permissions_cannotViewSelectedFlows,
    [DataRoleContentType.name]: Messages.permissions_cannotViewSelectedDataRoles,
    [ProjectContentType.name]: Messages.permissions_cannotViewSelectedProjects,
    [TableContentType.name]: Messages.permissions_cannotViewSelectedTables,
    [ViewContentType.name]: Messages.permissions_cannotViewSelectedViews,
    [WorkbookContentType.name]: Messages.permissions_cannotViewSelectedWorkbooks,
    [MetricContentType.name]: Messages.permissions_cannotViewSelectedMetrics,
    [PublishedConnectionContentType.name]: Messages.permissions_cannotViewSelectedPublishedConnections,
  };

  export function showEditPermissionsDialog(items: Set<any>, contentType: string): Promise<any> {

    if (contentType === DatasourceContentType.name && areAnyEmbeddedDatasourcesSelected(items)) {
      return Promise.resolve(alertCantShowPermissions(
        Messages.permissions_notShowingEmbeddedAndPublishedDataSources({
          ITEM_COUNT_LOC: Formatters.formatCount(items.size()),
        })
      ));
    }
    const authorizableType = getAuthorizableType(contentType);

    return GetActionableObjectsForAction.editPermissions({
      type: authorizableType,
      ids: items.keys(),
    }).then((result: IGetActionableObjectsResult) => {
      const ids = result.ids;
      const unactionableItems = items.clone();
      let actionableItems = new Set<INamedItem>((item: INamedItem) => item.id);
      _.each(ids, (id: string) => {
        const item = items.get(id);
        actionableItems.add(item);
        unactionableItems.remove(item);
      });

      // if there are any items in the section that the user isn't allowed to set permissions on,
      // we don't show the permissions panel
      if (unactionableItems.size() > 0) {
        return alertCantShowPermissions(
          getCannotViewAllSelectedMessage(contentType, items)
        ).result;
      }

      const serverService = getSessionManagementServiceInstance();
      const authorizables = actionableItems.clone();
      const flowsEnabled = serverService.flowsOnSiteEnabled();
      const editingFlowsEnabled = serverService.editingFlowsEnabled();

      const permissionsDialogProps: PermissionsDialogProps = {
        authorizables,
        contentType,
        flowsEnabled,
        editingFlowsEnabled,
      };

      return openPermissionsDialog(permissionsDialogProps);
    });
  }

  export function openPermissionsDialog(permissionsDialogProps: PermissionsDialogProps) {
    const dialogTitle = getPermissionsDialogTitle(permissionsDialogProps.contentType, permissionsDialogProps.authorizables);
    return ReactModalService.open({
      testId: 'permissions-dialog-react',
      type: DialogType.NormalWithTitleBar,
      dialogWidth: PermissionsDialogStyles.dialogContainerWidth,
      dialogContent: React.createElement(PermissionsDialog, permissionsDialogProps),
      showCloseButton: true,
      positioningBehavior: DialogPositioningBehavior.Centered,
      shouldReturnFocusOnClose: true,
      title: dialogTitle,
    });
  }

  export function getPermissionsDialogTitle(contentType: string, authorizables: Set<any>): string {
    const authorizableType = getAuthorizableType(contentType);
    if (authorizables) {
      const itemCount = authorizables.size();
      let itemName: string;
      let itemCountLoc: string;
      if (itemCount === 1) {
        itemName = authorizables.first().name;
      } else {
        itemCountLoc = Formatters.formatCount(itemCount);
      }

      // Using separate strings for single items and multiple items because
      // CJK languages don't have plural form so the title could look werid (defect 1158040)
      switch (authorizableType.toLowerCase()) {
        case AuthorizableType.DATABASE.toLowerCase():
          return itemCount === 1 ? Messages.permissionsForDatabase_single({ ITEM_NAME: itemName })
            : Messages.permissionsForDatabase_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: itemCountLoc });

        case AuthorizableType.DATAROLE.toLowerCase():
          return itemCount === 1 ? Messages.permissionsForDataRole_single({ ITEM_NAME: itemName })
            : Messages.permissionsForDataRole_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: itemCountLoc });

        case AuthorizableType.DATASOURCE.toLowerCase():
          return itemCount === 1 ? Messages.permissionsForDataSource_single({ ITEM_NAME: itemName })
            : Messages.permissionsForDataSource_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: itemCountLoc });

        case AuthorizableType.FLOW.toLowerCase():
          return itemCount === 1 ? Messages.permissionsForFlow_single({ ITEM_NAME: itemName })
            : Messages.permissionsForFlow_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: itemCountLoc });

        case AuthorizableType.METRIC.toLowerCase():
          return itemCount === 1 ? Messages.permissionsForMetric_single({ ITEM_NAME: itemName })
            : Messages.permissionsForMetric_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: itemCountLoc });

        case AuthorizableType.PUBLISHED_CONNECTION.toLowerCase():
          return itemCount === 1 ? Messages.permissionsForPublishedConnection_single({ ITEM_NAME: itemName })
          : Messages.permissionsForPublishedConnection_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: itemCountLoc });

        case AuthorizableType.PROJECT.toLowerCase():
          return itemCount === 1 ? Messages.permissionsForProject_single({ ITEM_NAME: itemName })
          : Messages.permissionsForProject_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: itemCountLoc });

        case AuthorizableType.TABLE.toLowerCase():
          return itemCount === 1 ? Messages.permissionsForTable_single({ ITEM_NAME: itemName })
            : Messages.permissionsForTable_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: itemCountLoc });

        case AuthorizableType.VIEW.toLowerCase():
          return itemCount === 1 ? Messages.permissionsForView_single({ ITEM_NAME: itemName })
          : Messages.permissionsForView_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: itemCountLoc });

        case AuthorizableType.WORKBOOK.toLowerCase():
          return itemCount === 1 ? Messages.permissionsForWorkbook_single({ ITEM_NAME: itemName })
            : Messages.permissionsForWorkbook_multiple({ ITEM_COUNT: itemCount, ITEM_COUNT_LOC: itemCountLoc });
      }
    }

    return Messages.permissions();
  }

  export function getLocParamsForTitleFromAuthorizables(authorizables: Set<any>) {
    const itemsCount = authorizables.size();
    return {
      ITEM_COUNT: itemsCount,
      ITEM_COUNT_LOC: Formatters.formatCount(itemsCount),
      ITEM_NAME: authorizables.first().name
    };
  }

  function alertCantShowPermissions(message: string) {
    return openReactAlertDialog({
      title: Messages.permissions_cannotViewPermissions(),
      message,
      dialogWidth: TabDialogWidth.width500px
    });
  }

  function getCannotViewAllSelectedMessage(contentType: string, unactionableItems: Set<INamedItem>): string {
    const count = unactionableItems.size();
    return CannotViewAllSelectedMessages[contentType](
      {
        ITEM_NAME: L10n.elideIfLong(unactionableItems.first().name),
        ITEM_COUNT: count,
        ITEM_COUNT_LOC: Formatters.formatCount(count),
      });
  }

  function areAnyEmbeddedDatasourcesSelected(datasources: Set<INamedItem>): boolean {
    return !!datasources.find((datasource: IDataSourceInfo) => !!datasource.workbook);
  }

}

export type EditPermissionsAction = typeof EditPermissionsAction;
